import React from 'react';
import Toolbar from '../toolbar';
import { Button } from '@progress/kendo-react-buttons';

import { GetResourceValue } from '../../../utils/game-document';
import { GameDocumentContext } from '../../../contexts/game-document';
import { appStore } from '../../../stores/app-store';
import { Theme as ThemeModel, ThemeColor } from '../../../types/theme';
import cloneDeep from 'lodash.clonedeep';
import { formatDate } from '@progress/kendo-intl';
import { uuid12 } from '../../../types/common-helper';
import { PostResourceAsync } from '../../../services/files';
import {
  GetThemeAsync,
  PostThemeAsync,
  PutThemeAsync
} from '../../../services/games';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { Theme } from '../resources';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { useToast } from '../../../contexts/toast-context';

export const defaultColors: ThemeColor[] = [
  {
    defaultIndex: 0,
    type: 'Primary',
    color: '#4698CB'
  },
  {
    defaultIndex: 1,
    type: 'Secondary',
    color: '#A8B6BE'
  },
  {
    defaultIndex: 2,
    type: 'Background & Text',
    color: '#003A64'
  },
  {
    defaultIndex: 3,
    type: 'Info',
    color: '#0DC5E8'
  },
  {
    defaultIndex: 4,
    type: 'Success',
    color: '#14BF8B'
  },
  {
    defaultIndex: 5,
    type: 'Warning',
    color: '#FEB333'
  }
];

export const addMissingColor = (colors: ThemeColor[]) => {
  const newColors: ThemeColor[] = defaultColors ?? [];

  newColors.forEach((newColor: ThemeColor) => {
    if (!colors?.find((x: ThemeColor) => x.type === newColor.type)) {
      colors.splice(newColor.defaultIndex, 0, newColor);
    }
  });

  return colors;
};

export const ThemePage: React.FC = () => {
  const { gameId } = useParams();
  const [state] = React.useContext(GameDocumentContext);
  const [isDataExist, setIsDataExist] = React.useState<boolean>(false);
  const [theme, setTheme] = React.useState<ThemeModel | undefined>();
  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();
  const { onToastOpen } = useToast();

  const uploadImage = async (imageBlob: string) => {
    try {
      const gameId = state.gameId;
      const gameVer = state.gameDocument?.version;
      const dateString = formatDate(new Date(), 'yyMMdd');
      const fileNamePrefix = `${gameId}-${dateString}-${gameVer}`;

      const blob = await fetch(imageBlob).then((r) => r.blob());
      const fileName = `${fileNamePrefix}-${uuid12()}`;
      const extractedBlob: File = new File([blob], fileName, {
        type: blob.type
      });

      const newResourceResponse = await PostResourceAsync(extractedBlob);
      return newResourceResponse.value;
    } catch (error) {
      console.log(error, '>>>>error');
    }
  };

  const showSavingNotification = (isSuccess: boolean) => {
    if (!isSuccess) {
      onToastOpen({
        body: <div>Theme failed to save.</div>,
        style: 'error'
      });
    }

    if (isSuccess) {
      onToastOpen({
        body: <div>Theme saved.</div>,
        style: 'success'
      });
    }
  };

  const onSaveTheme = async () => {
    if (!theme) return;
    try {
      appStore.showLoading();

      const newTheme: ThemeModel = cloneDeep(theme);
      if (theme.logoUrl) {
        const logo = await uploadImage(theme.logoUrl);
        if (logo) {
          newTheme.logoUrl = logo;
        }
      }

      if (theme.footerUrl) {
        const footer = await uploadImage(theme?.footerUrl ?? '');
        if (footer) {
          newTheme.footerUrl = footer;
        }
      }

      if (isDataExist) {
        await PutThemeAsync(state?.gameId!, newTheme);
      } else {
        await PostThemeAsync(state?.gameId!, newTheme);
      }
      setIsDataExist(true);
      showSavingNotification(true);
    } catch (error) {
      console.error(error, '>>>>err');
      showSavingNotification(false);
    } finally {
      appStore.hideLoading();
    }
  };

  const populateTheme = async () => {
    try {
      let theme: ThemeModel = await GetThemeAsync(parseInt(gameId ?? ''));
      theme.colors = addMissingColor(theme.colors!);
      setTheme(theme);
      setIsDataExist(true);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    // Set the page title only when gameDocument is loaded.
    if (state?.gameDocument) {
      document.title = `Theme - ${GetResourceValue(
        state.gameDocument,
        state.gameDocument.overview?.titleResId!
      )}`;
    }
  }, [state]);

  React.useEffect(() => {
    populateTheme();

    let match = matchPath(
      '/designer/:gameId/resources/theme/*',
      location.pathname
    );
    if (match) {
      setBreadcrumb([{ text: 'Theme', to: match.pathnameBase }]);
    }

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Theme'}
        showDefaultContent={false}
        buttonHelpSupport={{
          title: 'Theme guide',
          url: 'https://forum.catalystglobal.com/t/4613'
        }}>
        <Button themeColor={'primary'} className={'me-1'} onClick={onSaveTheme}>
          Save
        </Button>
      </Toolbar>
      <hr />
      <Theme theme={theme} setTheme={(theme) => setTheme(theme)} />
    </>
  );
};
